import React, { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { BlogPostType } from '../../types';
import { blogPosts } from './posts';
import { serviceGalleries } from '../../config/blogImages';

const gallerySets = {
  analytics: [
    'https://images.unsplash.com/photo-1454165205744-3b78555e5572?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1522199992905-29661a13f16b?auto=format&fit=crop&w=1600&q=80',
  ],
  ads: [
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1600&q=80',
  ],
  social: [
    'https://images.unsplash.com/photo-1520607162513-77705c0f0d4a?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1492724441997-5dc865305da7?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80',
  ],
  dev: [
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1492724441997-5dc865305da7?auto=format&fit=crop&w=1600&q=80',
  ],
  consulting: [
    'https://images.unsplash.com/photo-1515165562835-c4c1b0d2b2ab?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1454165205744-3b78555e5572?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1483478550801-ceba5fe50e8e?auto=format&fit=crop&w=1600&q=80',
  ],
  creative: [
    'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1498050108023-c5249f4df085?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&w=1600&q=80',
  ],
  video: [
    'https://images.unsplash.com/photo-1478720568477-152d9b164e26?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1521737604893-d14cc237f11d?auto=format&fit=crop&w=1600&q=80',
    'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?auto=format&fit=crop&w=1600&q=80',
  ],
};

const extraContent: Record<string, string[]> = {
  'ai-seo-2025-trends': [
    'Build topic clusters around questions, not just keywords. Structure answers with schema, FAQ markup, and concise summaries that language models can lift cleanly.',
    'Tighten technical hygiene: lightning-fast LCP, consistent internal links, and crawlable navigation so engines can surface definitive answers in overviews.',
    'Measure both classic SEO KPIs (rank, clicks, conversions) and answer visibility in AI surfaces. Pair Search Console with logs and AEO tracking to see coverage.',
  ],
  'google-ads-playbook': [
    'Start with intent tiers: exact match cores, phrase match for discovery, and negatives to guard brand. Map ad groups to single-intent themes for relevance.',
    'Ads need message match: keyword in headline, benefit in H2, proof in path, and a CTA that mirrors the landing page hero.',
    'Report on pipeline, not just CPC. Import conversions, connect offline events, and segment by new vs. returning to steer budget into profitable paths.',
  ],
  'meta-ads-blueprint': [
    'Lead with creatives: 3-5 hooks, 2 offers, and 2 formats (static + UGC) in a test pod. Kill fast, scale winners with budgets not bids.',
    'Use broad with exclusions plus remarketing. Layer simple first-party signals (engagers, site visitors) before complex stacks.',
    'Track post-lead quality: pass UTMs into CRM, score replies, and feed back high-quality events to improve optimization.',
  ],
  'youtube-ads-guide': [
    'Open strong in 3 seconds: problem, hook, or pattern interrupt. Move to credibility and one clear CTA by 25s.',
    'Target with custom intent, competitor placements, and remarketing. Split campaigns by awareness vs. demand capture.',
    'Measure lift with view-through conversions and brand lift surveys; align landing pages to the exact promise in the video.',
  ],
  'custom-web-dev-checklist': [
    'Define the job-to-be-done for each page: who it serves, what it must prove, and the action it must earn.',
    'Bake in performance: image budgets, edge caching, and critical CSS. Ship monitoring (RUM) so regressions surface fast.',
    'Integrate analytics, forms, and CRM from day one. Plan auth, roles, and backups so ops is boring and reliable.',
  ],
  'content-marketing-system': [
    'Use a research stack: customer calls, SERP mining, and internal SME interviews to build a quarterly topic map.',
    'Standardize production: briefs, outlines, drafts, QA, and on-page SEO. Publish with consistent CTAs tied to offers.',
    'Distribute in layers: email, social, partners, and paid boosts. Track assisted conversions to prove content ROI.',
  ],
  'traditional-seo-guide': [
    'Fix the crawl first: sitemaps, robots, canonical discipline, and clean URL patterns. Then tune Core Web Vitals.',
    'Ship intent-led pages: comparisons, how-tos, pricing, and implementation guides that satisfy searcher intent fully.',
    'Earn links through assets people want: data cuts, calculators, and industry templates. Outreach with relevance, not volume.',
  ],
  'ppc-foundations': [
    'Structure by intent: exact cores, alpha vs. beta campaigns, and shared negatives. Keep SKAGs rare; prefer tight theming.',
    'Ad copy templates: intent + benefit + proof + CTA. Always test paths and extensions alongside headlines.',
    'Use budgets as steering, bids as tuning. Layer audiences for bid mods and keep search term pruning weekly.',
  ],
  'bing-ads-advantage': [
    'Import Google builds, then localize: adjust match types, add LinkedIn profile targeting, and re-balance bids for desktop-heavy traffic.',
    'Expand with audience network placements, but control with exclusions and observation-only layers before committing.',
    'Track separately: Bing often converts at different times. Keep attribution windows aligned and watch assisted revenue.',
  ],
  'web-design-for-performance': [
    'Above the fold: clear headline, specific subhead, trust signals, and a single CTA. Avoid carousels and clutter.',
    'Design for momentum: scannable sections, sticky CTAs, and proof (logos, quotes, numbers) near every decision point.',
    'Validate with data: session replays, form analytics, and A/B tests. Ship small improvements weekly.',
  ],
  'wordpress-vs-shopify': [
    'WordPress suits content-heavy, flexible sites. Shopify wins for ecommerce speed, checkout, and app ecosystem.',
    'Consider ops: hosting, security, and maintenance overhead for WP vs. app costs and theme constraints on Shopify.',
    'Choose based on catalog complexity, merchandising needs, and internal skills. Avoid switching platforms mid-scale.',
  ],
  'app-development-roadmap': [
    'Define the smallest lovable product: core jobs, must-have flows, and one metric to win before adding features.',
    'Pick a stack that matches team strengths and release cadence. Instrument analytics and crash reporting from sprint one.',
    'Plan releases: weekly betas, feedback loops, and backlog grooming tied to usage and retention, not opinions.',
  ],
  'whatsapp-api-marketing': [
    'Capture consent with value: shipping updates, drop alerts, or concierge support. Store opt-ins with source and timestamp.',
    'Use templates smartly: welcome, nurture, reactivation. Keep messages short, with quick replies and a single CTA.',
    'Route to humans when needed and log outcomes in CRM. Monitor block rates and adjust cadence to stay compliant.',
  ],
  'social-media-system': [
    'Define 4-6 content pillars tied to offers. Batch scripts, captions, and design to maintain weekly consistency.',
    'Repurpose: long posts to carousels, clips to reels, and comments to FAQs. Keep CTAs aligned to one offer per month.',
    'Measure leading signals: saves, replies, profile visits, then downstream site traffic and assisted conversions.',
  ],
  'platform-specific-creatives': [
    'Facebook: bold hooks, fast cuts, and product-in-use within 3 seconds. Use square and 4:5 for feed and reels.',
    'Instagram: carousels with micro-stories and reel hooks that mirror trending sounds. CTA in-frame by 5 seconds.',
    'YouTube: story arcs (hook, claim, proof, CTA) and visual pacing. Add captions and keep branding subtle.',
  ],
  'facebook-ads-leadgen': [
    'Use native lead forms with qualifying questions. Sync to CRM instantly and send a fast auto-reply with next steps.',
    'Segment remarketing by depth: form opens, submissions, and site visits. Pair with click campaigns to warm audiences.',
    'Score leads by source campaign and creative. Feed high-quality events back to improve optimization.',
  ],
  'instagram-growth-2025': [
    'Anchor on a weekly cadence: 3 reels, 2 carousels, 1 story series. Mix education, proof, and offers.',
    'Collaborate with creators for credibility. Use trending sounds carefully; keep brand voice consistent.',
    'Drive to owned channels: link in bio with a single CTA, remarket engagers, and track saves/replays as intent.',
  ],
  'linkedin-b2b-pipeline': [
    'Optimize profiles as landing pages: clear headline, proof, and offer. Post consistently with POV, not press releases.',
    'Run thought-leadership ads to warm audiences, then use conversation ads or InMail for polite outreach.',
    'Measure meetings booked and revenue, not likes. Attribute via UTMs and CRM stages to defend spend.',
  ],
  'pinterest-ecommerce': [
    'Design pins for search: keyworded titles, rich pins, and seasonal boards. Use lifestyle imagery that shows use cases.',
    'Test idea pins for engagement, then promote top performers. Keep CTAs soft but clear.',
    'Follow through with remarketing and catalog sync to keep product availability accurate.',
  ],
  'tiktok-ads-playbook': [
    'Script like UGC: hook, pain, quick demo, proof, CTA. Keep cuts tight and captions bold.',
    'Use Spark Ads to boost winning organic posts. Test 5-10 hooks before scaling budgets.',
    'Moderate comments, pin the best, and use them as social proof in the next creative batch.',
  ],
  'threads-brand-voice': [
    'Post short, conversational prompts. Encourage replies with low-friction questions and react fast.',
    'Build a tone guide: what you talk about, how you respond, and where you avoid debates.',
    'Repurpose only if it feels native—rewrite, don’t copy-paste. Celebrate community responses often.',
  ],
  'whatsapp-marketing-flows': [
    'Map the lifecycle: opt-in, onboarding, engagement, reactivation. Keep each flow under three quick messages.',
    'Personalize with tags: product interest, recency, and purchase value. Avoid broad blasts.',
    'Test send times and monitor read/reply rates. Rotate templates to prevent fatigue.',
  ],
  'consulting-roi': [
    'Define success upfront: a lighthouse win in 30-60 days, a dashboard everyone reads, and owners for each metric.',
    'Work in weekly rhythms: propose, ship, measure, decide. Keep stakeholders aligned with simple notes, not decks.',
    'Quantify impact: revenue, savings, or risk reduced. Show counterfactuals to prove the value of changes.',
  ],
  'white-label-delivery': [
    'Onboard partners with clear SLAs, intake forms, and examples. Standardize briefs to reduce rework.',
    'Automate status: shared dashboards, weekly rollups, and ticket-level notes. Make delivery predictable.',
    'Protect brand safety: QA checklists, approvals, and incident playbooks. Document everything for continuity.',
  ],
};

const BlogPostPage: React.FC = () => {
  const { slug } = useParams();

  const posts: BlogPostType[] = useMemo(() => {
    const fromLocal = localStorage.getItem('blogPosts');
    const stored = fromLocal ? (JSON.parse(fromLocal) as BlogPostType[]) : [];
    return stored.length ? stored : blogPosts;
  }, []);

  const post = posts.find(p => p.slug === slug);

  // Split content by paragraphs for better formatting
  const richParagraphs = post ? post.content.split('\n\n').filter(p => p.trim()) : [];
  
  // Get gallery images based on slug or category
  const galleryImages = post ? (() => {
    const slugKey = post.slug.replace(/-services?-guide/, '').replace(/-/g, '');
    if (slugKey.includes('seo')) return serviceGalleries.seo;
    if (slugKey.includes('ppc') || slugKey.includes('ads')) return serviceGalleries.ppc;
    if (slugKey.includes('social')) return serviceGalleries.socialMedia;
    if (slugKey.includes('web') || slugKey.includes('design')) return serviceGalleries.webDesign;
    if (slugKey.includes('content')) return serviceGalleries.contentMarketing;
    if (slugKey.includes('consult')) return serviceGalleries.consulting;
    if (slugKey.includes('email')) return serviceGalleries.emailMarketing;
    if (slugKey.includes('ai')) return serviceGalleries.aiServices;
    return serviceGalleries.seo;
  })() : [];

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-2">Article not found</h1>
          <p className="text-gray-600 mb-4">The blog post you’re looking for doesn’t exist.</p>
          <Link to="/blog" className="text-primary-600 font-semibold">Back to Blog</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-purple-200/50 to-blue-100/50 rounded-full blur-3xl"
          animate={{ y: [0, 40, 0], x: [0, 30, 0] }}
          transition={{ duration: 12, repeat: Infinity, ease: 'easeInOut' }}
        />
        <motion.div
          className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-200/50 to-cyan-100/50 rounded-full blur-3xl"
          animate={{ y: [0, -40, 0], x: [0, -30, 0] }}
          transition={{ duration: 15, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        />
      </div>

      <section className="pt-24 pb-10 px-4 relative z-10">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
            className="bg-white/90 backdrop-blur rounded-3xl shadow-2xl overflow-hidden border border-slate-100 hover:border-primary-200 transition-colors"
          >
            {post.image && (
              <motion.div
                className="relative h-96 overflow-hidden"
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.6 }}
              >
                <motion.img
                  src={post.image}
                  alt={post.title}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.1 }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                />
                <motion.div
                  className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/30 to-transparent"
                  whileHover={{ opacity: 0.8 }}
                  transition={{ duration: 0.3 }}
                />
                <motion.div
                  className="absolute bottom-6 left-6 flex flex-wrap items-center gap-3 text-sm text-white"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <motion.span
                    className="px-4 py-2 rounded-full bg-gradient-to-r from-primary-500 to-primary-600 border border-white/20 font-semibold shadow-lg"
                    whileHover={{ scale: 1.1, boxShadow: '0 10px 30px rgba(99,102,241,0.3)' }}
                  >
                    {post.category}
                  </motion.span>
                  <motion.span
                    className="px-4 py-2 rounded-full bg-white/15 border border-white/30 backdrop-blur font-medium"
                    whileHover={{ scale: 1.05 }}
                  >
                    {new Date(post.publishedAt).toLocaleDateString()}
                  </motion.span>
                </motion.div>
              </motion.div>
            )}

            <div className="p-8 md:p-12">
              <motion.div
                className="flex items-center justify-between gap-4 text-sm text-gray-500 mb-4"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <motion.span whileHover={{ x: -2 }}>By {post.author}</motion.span>
                <motion.div whileHover={{ x: -4 }}>
                  <Link to="/blog" className="text-primary-600 font-semibold hover:text-primary-700">← Back to Blog</Link>
                </motion.div>
              </motion.div>

              <motion.h1
                className="text-4xl md:text-5xl font-extrabold leading-tight mb-6 bg-gradient-to-r from-slate-900 via-purple-800 to-slate-900 bg-clip-text text-transparent"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1, duration: 0.5 }}
              >
                {post.title}
              </motion.h1>

              {post.tags && post.tags.length > 0 && (
                <motion.div
                  className="flex flex-wrap gap-2 mb-8"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.15, staggerChildren: 0.05 }}
                >
                  {post.tags.map((tag, idx) => (
                    <motion.span
                      key={tag}
                      className="px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-primary-50 to-primary-100 text-primary-700 border border-primary-200 hover:border-primary-400 transition-colors"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: idx * 0.05 }}
                      whileHover={{ scale: 1.05, y: -2 }}
                    >
                      {tag}
                    </motion.span>
                  ))}
                </motion.div>
              )}

              <motion.article
                className="space-y-4 mb-10 text-gray-700"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                {richParagraphs.map((paragraph, idx) => (
                  <motion.p
                    key={idx}
                    className="text-lg leading-relaxed"
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    viewport={{ once: true, margin: '-100px' }}
                  >
                    {paragraph}
                  </motion.p>
                ))}
              </motion.article>

              {galleryImages.length > 0 && (
                <motion.div
                  className="mt-14"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: true }}
                >
                  <motion.h3
                    className="text-2xl font-bold mb-6 bg-gradient-to-r from-slate-900 to-primary-700 bg-clip-text text-transparent"
                    whileHover={{ scale: 1.02 }}
                  >
                    Inspiration & Metrics
                  </motion.h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {galleryImages.map((img, idx) => (
                      <motion.div
                        key={img}
                        className="relative rounded-2xl overflow-hidden shadow-md border border-slate-100 hover:border-primary-200 transition-colors"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: idx * 0.1, ease: 'easeOut' }}
                        whileHover={{
                          scale: 1.03,
                          boxShadow: '0 20px 40px rgba(99,102,241,0.15)',
                        }}
                        viewport={{ once: true, margin: '-100px' }}
                      >
                        <motion.img
                          src={img}
                          alt="Detail visual"
                          className="w-full h-48 object-cover"
                          whileHover={{ scale: 1.08 }}
                          transition={{ duration: 0.6, ease: 'easeOut' }}
                        />
                        <motion.div
                          className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent"
                          whileHover={{ opacity: 0.5 }}
                          transition={{ duration: 0.3 }}
                        />
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default BlogPostPage;